import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  currentUser,
  login,
  logout,
  register,
  confirmRegistration,
  requestPasswordReset,
  confirmPasswordReset,
} from '../../lib/auth.js';
import { apiClient } from '../../lib/api-client.js';
import { useSessionTimeout } from './useSessionTimeout.js';
import SessionTimeoutModal from './SessionTimeoutModal.js';
import { useUIStore } from '../../store/ui.store.js';

interface AuthUser {
  userId: string;
  username: string;
  email: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  signUp: (email: string, password: string, displayName: string) => Promise<void>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (email: string, code: string, newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();
  const setActiveList = useUIStore((s) => s.setActiveList);

  const bootstrapSession = useCallback(async () => {
    const cognito = await currentUser();
    if (cognito) {
      setUser({ userId: cognito.userId, username: cognito.username, email: cognito.email });
    } else {
      setUser(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    bootstrapSession();
  }, [bootstrapSession]);

  async function signIn(email: string, password: string) {
    await login(email, password);
    const cognito = await currentUser();
    if (cognito) {
      setUser({ userId: cognito.userId, username: cognito.username, email: cognito.email });
      // Upsert profile on login. displayName is required by the backend the
      // first time a user's profile row is created; omitting it made every
      // first-ever login 400 (silently, since the request is best-effort).
      await apiClient.put('/v1/users/me', { email, displayName: cognito.username }).catch(() => {});
    }
  }

  async function signOut() {
    await logout();
    // React Query's cache is keyed by resource (e.g. ['tasks', 'all']), not by user —
    // without this, the next user to sign in on this tab sees the previous user's
    // cached tasks/lists until a refetch happens to overwrite them.
    await queryClient.cancelQueries();
    queryClient.clear();
    setActiveList(null);
    setUser(null);
  }

  async function signUp(email: string, password: string, displayName: string) {
    await register(email, password, displayName);
  }

  async function confirmSignUp(email: string, code: string) {
    await confirmRegistration(email, code);
  }

  async function forgotPassword(email: string) {
    await requestPasswordReset(email);
  }

  async function resetPassword(email: string, code: string, newPassword: string) {
    await confirmPasswordReset(email, code, newPassword);
  }

  const { warningVisible, secondsLeft, staySignedIn } = useSessionTimeout(!!user, signOut);

  return (
    <AuthContext.Provider
      value={{ user, loading, signIn, signOut, signUp, confirmSignUp, forgotPassword, resetPassword }}
    >
      {children}
      {warningVisible && (
        <SessionTimeoutModal
          secondsLeft={secondsLeft}
          onStaySignedIn={staySignedIn}
          onSignOut={signOut}
        />
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
